import { useEffect, useState } from "react";
import { languageStore } from "../state/language";
import { findLanguage } from "./languages";

// 화면에 표시된 한국어 문구를 언어별 사전으로 바꿔 보여준다.
// 기존 컴포넌트를 고치지 않고 사이트 전체에 적용하기 위한 방식이다.
// - 사전 키: 앞뒤 공백을 지우고 연속 공백을 하나로 줄인 한국어 문구
// - 사전에 없는 문구(예: 게시판 API로 받은 게시물)는 원문 그대로 둔다
// - 한국어(KR)로 돌아오면 저장해 둔 원문으로 복원한다
// - 언어별 사전은 그 언어를 고를 때만 내려받는다(초기 로딩 속도 유지)
const loaded = {};
const loadDict = (file) =>
  loaded[file] || (loaded[file] = import(`./dict/${file}.json`).then((m) => m.default || m).catch(() => ({})));
const ATTRS = ["placeholder", "alt", "title", "aria-label"];
const HANGUL = /[가-힣]/;
const norm = (s) => s.replace(/\s+/g, " ").trim();

// 사전 조회: 그대로 → 앞뒤 따옴표를 뗀 문구(제목 아래 보조문구) → 날짜 형식 순
const DATE = /^(\d{4})년 (\d{1,2})월 (\d{1,2})일$/;
const formatDate = (lang, y, m, d) =>
  lang === "EN" ? new Date(y, m - 1, d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
  : lang === "VI" ? `ngày ${d} tháng ${m} năm ${y}`
  : `${y}年${m}月${d}日`;
const lookup = (dict, lang, text) => {
  const key = norm(text);
  if (dict[key]) return dict[key];
  const q = key.match(/^"([\s\S]*)"$/);
  if (q && dict[q[1]]) return `"${dict[q[1]]}"`;
  const dm = key.match(DATE);
  if (dm) return formatDate(lang, +dm[1], +dm[2], +dm[3]);
  return null;
};

const originalText = new WeakMap(); // Text 노드 → 원문
const shownText = new WeakMap(); // Text 노드 → 번역해서 넣은 값 (React가 값을 바꿨는지 판별용)
const originalAttr = new WeakMap(); // Element → {attr: 원문}

const skip = (node) => {
  const el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return !!(el && el.closest("[data-no-translate]"));
};

const translateText = (node, dict, lang) => {
  if (skip(node)) return;
  // React가 번역된 노드의 값을 새 한국어로 바꾼 경우, 새 값을 원문으로 다시 잡는다
  if (originalText.has(node) && node.nodeValue !== shownText.get(node)) {
    originalText.delete(node);
    shownText.delete(node);
  }
  const source = originalText.has(node) ? originalText.get(node) : node.nodeValue;
  if (!source || !HANGUL.test(source)) return;
  const hit = dict && lookup(dict, lang, source);
  if (hit) {
    if (!originalText.has(node)) originalText.set(node, source);
    const lead = source.match(/^\s*/)[0];
    const tail = source.match(/\s*$/)[0];
    const next = lead + hit + tail;
    shownText.set(node, next);
    if (node.nodeValue !== next) node.nodeValue = next;
  } else if (originalText.has(node)) {
    node.nodeValue = originalText.get(node);
    originalText.delete(node);
    shownText.delete(node);
  }
};

const translateAttrs = (el, dict, lang) => {
  if (skip(el)) return;
  for (const attr of ATTRS) {
    if (!el.hasAttribute || !el.hasAttribute(attr)) continue;
    const saved = originalAttr.get(el) || {};
    const source = saved[attr] ?? el.getAttribute(attr);
    if (!HANGUL.test(source)) continue;
    const hit = dict && lookup(dict, lang, source);
    if (hit) {
      saved[attr] = source;
      originalAttr.set(el, saved);
      if (el.getAttribute(attr) !== hit) el.setAttribute(attr, hit);
    } else if (saved[attr] !== undefined) {
      el.setAttribute(attr, saved[attr]);
      delete saved[attr];
    }
  }
};

const walk = (root, dict, lang) => {
  if (root.nodeType === Node.TEXT_NODE) return translateText(root, dict, lang);
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  if (root.closest && root.closest("script,style,[data-no-translate]")) return;
  translateAttrs(root, dict, lang);
  // data-no-translate 영역(언어 선택 목록 등)은 하위까지 건너뛴다
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) =>
      n.nodeType === Node.ELEMENT_NODE && n.hasAttribute("data-no-translate") ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
  });
  let n = tw.nextNode();
  while (n) {
    if (n.nodeType === Node.TEXT_NODE) translateText(n, dict, lang);
    else translateAttrs(n, dict, lang);
    n = tw.nextNode();
  }
};

const titleState = { original: "", shown: null };

const DomTranslator = () => {
  const { language } = languageStore();
  const [dict, setDict] = useState(null);

  useEffect(() => {
    const info = findLanguage(language);
    document.documentElement.lang = info.html;
    document.documentElement.dir = info.rtl ? "rtl" : "ltr";
    if (!info.file) {
      setDict(null);
      return undefined;
    }
    let alive = true;
    loadDict(info.file).then((d) => alive && setDict({ language, d }));
    return () => {
      alive = false;
    };
  }, [language]);

  useEffect(() => {
    const active = dict && dict.language === language ? dict.d : null;
    if (language !== "KR" && !active) return undefined; // 사전 내려받는 중
    const body = document.body;
    walk(body, active, language);
    // 제목은 번역된 값으로 덮어쓰므로 원문을 따로 기억해 두고, 언어가 바뀌면 원문에서 다시 번역한다
    const translateTitle = () => {
      const t = document.title;
      if (t !== titleState.shown) titleState.original = t; // 페이지가 새 제목을 설정함
      const src = titleState.original;
      const next = active ? src.split(" - ").map((p) => lookup(active, language, p) || p).join(" - ") : src;
      titleState.shown = next;
      if (next !== t) document.title = next;
    };
    translateTitle();
    if (!active) return undefined;

    let busy = false;
    const observer = new MutationObserver((mutations) => {
      if (busy) return;
      busy = true;
      for (const m of mutations) {
        if (m.type === "characterData") translateText(m.target, active, language);
        else if (m.type === "attributes") translateAttrs(m.target, active, language);
        else m.addedNodes.forEach((n) => walk(n, active, language));
      }
      translateTitle();
      busy = false;
    });
    observer.observe(body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    const titleEl = document.querySelector("title");
    const titleObserver = new MutationObserver(translateTitle);
    if (titleEl) titleObserver.observe(titleEl, { childList: true });
    return () => {
      observer.disconnect();
      titleObserver.disconnect();
    };
  }, [language, dict]);

  return null;
};
export default DomTranslator;
